import useStore from 'Hooks/useStore';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { VoucherTicket } from '../../PurchaseGiftCertificate/VoucherTicket';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import {
	GiftVoucherDto,
	startConvertVoucherToManualRefund,
	startUpdatingVoucher,
	startVoidingVoucher,
} from './GiftVouchersUtils';

export interface GiftVoucherCardProps {
	giftVoucher: GiftVoucherDto;
	refresh: () => void;
	justClaimed?: boolean;
}

export const GiftVoucherCard = ({ giftVoucher, refresh, justClaimed }: GiftVoucherCardProps) => {
	const store = useStore();
	const { isStaff, isManager } = store;
	const [viewModalOpen, setViewModalOpen] = useState(false);
	const showNewTag = useRef(false);

	useEffect(() => {
		if (justClaimed) {
			showNewTag.current = true;
		}
	}, [justClaimed]);

	const isExpired = giftVoucher.status === 'EXPIRED' || giftVoucher.status === 'USEDEXPIRED';
	const isUsed = giftVoucher.status === 'USED';
	const isDisabled = giftVoucher.disabled === true;
	const isActive = !isExpired && !isUsed && !isDisabled;
	const isGift = !!giftVoucher.recipientEmail;

	let status: 'active' | 'redeemed' | 'expired' | 'voided' | 'refunded' = 'active';
	// A credit paid back out as a refund reads as Refunded; an outright void still reads as Voided.
	if (isDisabled) status = giftVoucher.refunded === true ? 'refunded' : 'voided';
	else if (isExpired) status = 'expired';
	else if (isUsed) status = 'redeemed';

	const expiryText = `${isExpired ? 'Expired' : 'Expires'} ${moment(giftVoucher.expiryDate).format('DD MMM YYYY')}`;

	let originLabel = 'Refund credit';
	if (giftVoucher.recipientEmail) originLabel = 'Gifted';
	else if (giftVoucher.invoiced) originLabel = 'Invoiced';
	else if (giftVoucher.price) originLabel = 'Paid';

	// convert-to-manual-refund is still a Manager role check server-side, not ManageCreditVouchers.
	const canConvertToRefund =
		isManager && isActive && giftVoucher.remainingValue > 0 && !!giftVoucher.canConvertToRefund;
	const canManageVouchers = store.can('ManageCreditVouchers');
	// Matches the staff vouchers list exactly. Deliberately NOT isActive: an expired voucher stays
	// editable so its expiry can be extended, which is the main reason to edit one.
	const canEdit = canManageVouchers && !isDisabled && !isUsed;
	// Also matches the staff vouchers list. Expired is excluded here where it isn't for editing:
	// voiding an already-unusable voucher achieves nothing and the action is irreversible.
	const canVoid = canManageVouchers && !isDisabled && !isExpired && !isUsed;

	const handleEdit = async () => {
		setViewModalOpen(false);
		await startUpdatingVoucher({
			userId: giftVoucher.userId,
			refresh,
			voucherId: giftVoucher.id,
			voucherAmount: giftVoucher.initialValue,
			amountRemaining: giftVoucher.remainingValue,
			expiryDate: giftVoucher.expiryDate,
			recipientEmail: giftVoucher.recipientEmail,
			hasUser: !!giftVoucher.userId,
		});
	};

	const handleVoid = async () => {
		setViewModalOpen(false);
		await startVoidingVoucher({ voucherId: giftVoucher.id, code: giftVoucher.code, refresh });
	};

	const handleConvertToRefund = async () => {
		setViewModalOpen(false);
		await startConvertVoucherToManualRefund({
			voucherId: giftVoucher.id,
			code: giftVoucher.code,
			remainingValue: giftVoucher.remainingValue,
			refresh,
		});
	};

	return (
		<>
			{/* Card on credits tab */}
			<VoucherTicket
				isGift={isGift}
				code={giftVoucher.code}
				amount={giftVoucher.remainingValue}
				status={status}
				expiryText={expiryText}
				hasMessage={!!giftVoucher.message}
				inactive={!isActive}
				justClaimed={justClaimed}
				isNew={showNewTag.current}
				onClick={() => setViewModalOpen(true)}
			/>

			{/* View Modal */}
			<NewModal
				isOpen={viewModalOpen}
				label="Voucher Summary"
				onRequestClose={() => setViewModalOpen(false)}
				modalSize="sm"
			>
				<NewModal.Header>
					<NewModal.Title>Voucher summary</NewModal.Title>
					<Button
						className="new-modal--close"
						icon={{ icon: 'close', iconPos: 'icon-left' }}
						display={Display.Text}
						colors={Colors.Alternate}
						onClick={() => setViewModalOpen(false)}
						labelVisible={false}
					/>
				</NewModal.Header>
				<NewModal.Body>
					<VoucherTicket
						isGift={isGift}
						code={giftVoucher.code}
						amount={giftVoucher.remainingValue}
						initialAmount={giftVoucher.initialValue}
						status={status}
						expiryText={expiryText}
						hasMessage={!!giftVoucher.message}
						message={giftVoucher.message}
						showMessage
						inactive={!isActive}
					/>
					{isStaff && (
						<div className="voucher-card__activity">
							<p className="voucher-card__activity-header">Activity</p>
							<div className="voucher-card__activity-row">
								<span className="voucher-card__activity-date">
									{giftVoucher.createdDate &&
										`${moment(giftVoucher.createdDate).format('DD MMM YYYY')}, ${formatStandardTime(giftVoucher.createdDate)}`}
								</span>
								<span>
									{originLabel}
									{giftVoucher.transactionId && ` (#${giftVoucher.transactionId})`}
								</span>
								<span>{formatCurrency(giftVoucher.price ?? giftVoucher.initialValue)}</span>
							</div>
							{giftVoucher.refunded && (
								<div className="voucher-card__activity-row">
									<span className="voucher-card__activity-date">
										{giftVoucher.refundedDate &&
											`${moment(giftVoucher.refundedDate).format('DD MMM YYYY')}, ${formatStandardTime(giftVoucher.refundedDate)}`}
									</span>
									<span>Refunded to customer</span>
									<span>- {formatCurrency(giftVoucher.remainingValue)}</span>
								</div>
							)}
							{giftVoucher.giftCertificateUsages.map(usage => (
								<div className="voucher-card__activity-row" key={usage.humanReadableId}>
									<span className="voucher-card__activity-date">
										{usage.date &&
											`${moment(usage.date).format('DD MMM YYYY')}, ${formatStandardTime(usage.date)}`}
									</span>
									<span>Used (#{usage.humanReadableId})</span>
									<span>- {formatCurrency(usage.amount)}</span>
								</div>
							))}
						</div>
					)}
				</NewModal.Body>
				{/*
				 * Only the real actions live here — closing is the header cross, an overlay click or
				 * Esc. Two tiers rather than a row of buttons: .new-modal__actions gives every .btn
				 * flex-grow: 1, so three side by side in an `sm` modal are equally weighted and equally
				 * cramped. Edit is the everyday action and takes the full-width solid button; the two
				 * that permanently end the voucher sit under it as smaller text actions, where they are
				 * one click away but never the thing a hurried hand lands on. Separated by space alone:
				 * their colours already tell them apart, and a divider rule between two text buttons
				 * reads as a stray border rather than as structure.
				 *
				 * canVoid is a subset of canEdit. canConvertToRefund is gated separately, so a manager
				 * without ManageCreditVouchers sees the secondary row on its own.
				 */}
				{(canEdit || canConvertToRefund) && (
					<NewModal.Footer className="voucher-card__actions">
						{canEdit && (
							<Button display={Display.Solid} colors={Colors.Primary} newStyles onClick={handleEdit}>
								Edit voucher
							</Button>
						)}
						{(canConvertToRefund || canVoid) && (
							<div className="voucher-card__actions-secondary">
								{canConvertToRefund && (
									// Black, not Grey: .btn--text.btn--grey resolves to $grey-3, which is a
									// border colour and unreadable as text. .btn--outline.btn--grey overrides
									// to $color-high-emphasis for the same reason; the text variant does not,
									// because EventOccurrencesTab relies on grey text reading as inactive.
									<Button
										display={Display.Text}
										colors={Colors.Black}
										sizes={Sizes.Small}
										newStyles
										onClick={handleConvertToRefund}
									>
										Convert to refund
									</Button>
								)}
								{canVoid && (
									<Button
										display={Display.Text}
										colors={Colors.Red}
										sizes={Sizes.Small}
										newStyles
										onClick={handleVoid}
									>
										Void voucher
									</Button>
								)}
							</div>
						)}
					</NewModal.Footer>
				)}
			</NewModal>
		</>
	);
};
